import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Search, Phone, MapPin, Star, Users, TrendingUp, 
  CheckCircle, XCircle, Building2, Filter, RefreshCw,
  ArrowUpDown, FileText, Mic
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProvidersManager() {
  const [providers, setProviders] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchLogs, setSearchLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('search_count');
  const [activeTab, setActiveTab] = useState('providers');

  useEffect(() => {
    fetchData();
  }, [statusFilter, cityFilter, sortBy]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('healthbook_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch providers
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (cityFilter) params.append('city', cityFilter);
      params.append('sort_by', sortBy);
      params.append('limit', '100');

      const providersRes = await fetch(`${API_URL}/api/admin/healthcare-providers?${params}`, { headers });
      if (providersRes.ok) {
        const data = await providersRes.json();
        setProviders(data.providers || []);
        setStats(data.stats || null);
      }

      // Fetch search logs
      const logsRes = await fetch(`${API_URL}/api/admin/voice-search-logs?limit=20`, { headers });
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setSearchLogs(logsData.logs || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const convertToPartner = async (providerId) => {
    try {
      const token = localStorage.getItem('healthbook_token');
      const res = await fetch(`${API_URL}/api/admin/healthcare-providers/${providerId}/convert-to-partner`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error converting provider:', error);
    }
  };

  const filteredProviders = providers.filter(p => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return p.name?.toLowerCase().includes(term) || 
             p.city?.toLowerCase().includes(term) ||
             p.phone?.includes(term);
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-blue-600" />
            Gestion des Providers
          </h1>
          <p className="text-gray-600 mt-2">
            Base de données des professionnels de santé découverts via les recherches vocales
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Providers</p>
                    <p className="text-2xl font-bold">{stats.total}</p>
                  </div>
                  <Users className="w-10 h-10 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Partenaires</p>
                    <p className="text-2xl font-bold text-green-600">{stats.partners}</p>
                  </div>
                  <CheckCircle className="w-10 h-10 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Non-Partenaires</p>
                    <p className="text-2xl font-bold text-orange-600">{stats.non_partners}</p>
                  </div>
                  <XCircle className="w-10 h-10 text-orange-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Recherches Vocales</p>
                    <p className="text-2xl font-bold text-purple-600">{stats.total_searches}</p>
                  </div>
                  <Mic className="w-10 h-10 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'providers' ? 'default' : 'outline'}
            onClick={() => setActiveTab('providers')}
            className="flex items-center gap-2"
          >
            <Building2 className="w-4 h-4" />
            Providers ({providers.length})
          </Button>
          <Button
            variant={activeTab === 'searches' ? 'default' : 'outline'}
            onClick={() => setActiveTab('searches')}
            className="flex items-center gap-2"
          >
            <Mic className="w-4 h-4" />
            Recherches Vocales
          </Button>
        </div>

        {activeTab === 'providers' && (
          <>
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Rechercher par nom, ville, téléphone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border rounded-md px-3 py-2 bg-white"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="partenaire">Partenaires</option>
                    <option value="non_partenaire">Non-Partenaires</option>
                  </select>
                  
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="border rounded-md px-3 py-2 bg-white"
                  >
                    <option value="search_count">Plus recherchés</option>
                    <option value="rating">Meilleure note</option>
                    <option value="name">Alphabétique</option>
                    <option value="last_seen">Plus récents</option>
                  </select>
                  
                  <Button variant="outline" onClick={fetchData} className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Actualiser
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Providers List */}
            <div className="grid gap-4">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Chargement...</div>
              ) : filteredProviders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">Aucun provider trouvé</div>
              ) : (
                filteredProviders.map((provider) => (
                  <Card key={provider.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">{provider.name}</h3>
                            <Badge 
                              variant={provider.status === 'partenaire' ? 'default' : 'secondary'}
                              className={provider.status === 'partenaire' ? 'bg-green-500' : 'bg-orange-500'}
                            >
                              {provider.status === 'partenaire' ? 'Partenaire' : 'Non-Partenaire'}
                            </Badge>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            {provider.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {provider.city}
                              </span>
                            )}
                            {provider.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-4 h-4" />
                                {provider.phone}
                              </span>
                            )}
                            {provider.rating && (
                              <span className="flex items-center gap-1">
                                <Star className="w-4 h-4 text-yellow-500" />
                                {provider.rating}/5 ({provider.rating_count} avis)
                              </span>
                            )}
                            {provider.specialty && (
                              <span className="flex items-center gap-1">
                                <FileText className="w-4 h-4" />
                                {provider.specialty}
                              </span>
                            )}
                          </div>
                          
                          <div className="mt-2 text-xs text-gray-400">
                            Recherché {provider.search_count || 1} fois • 
                            Source: {provider.source || 'google_places'}
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          {provider.status !== 'partenaire' && (
                            <Button
                              size="sm"
                              onClick={() => convertToPartner(provider.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Convertir en Partenaire
                            </Button>
                          )}
                          {provider.phone && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`tel:${provider.phone}`)}
                            >
                              <Phone className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </>
        )}

        {activeTab === 'searches' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mic className="w-5 h-5" />
                Historique des Recherches Vocales
              </CardTitle>
            </CardHeader>
            <CardContent>
              {searchLogs.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Aucune recherche enregistrée</p>
              ) : (
                <div className="space-y-3">
                  {searchLogs.map((log, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">
                          {log.specialty || 'Médecin'} à {log.city}
                        </p>
                        <p className="text-sm text-gray-500">
                          {log.results_count} résultats • {log.partners_found} partenaires
                        </p>
                      </div>
                      <div className="text-right text-sm text-gray-400">
                        {new Date(log.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
